import {
  ChilliCheckbox,
  ChilliCheckboxGroup,
  ChilliInput,
  ChilliMultiselect,
  ChilliRadioGroup,
  ChilliSelect
} from '@group-gti/chilli-design-system-react';
import isEmpty from 'lodash/isEmpty';
import size from 'lodash/size';
import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { FieldInfo, FieldValue } from '../../app/user/profile/profileUtils';
import FileField from './FileField';
import { getValidator } from './ValidatorLibrary';

type Props = {
  field: FieldInfo;
  userData: FieldValue;
  methods: UseFormReturn;
  booleanAsCheckbox: boolean;
  displayLabel: boolean;
};

/**
 * Determines how a field should display and render
 * @return {JSX}
 */
const UserserviceField = ({ field, userData, methods, booleanAsCheckbox = false, displayLabel = true }: Props) => {
  const {
    control,
    formState: { errors },
    watch
  } = methods;
  const fieldErrors = errors[field.name] ?? null;
  const selectedValue = watch(field.name);

  // change display when large amount of options available set here
  const highOptionCountNumber = process.env.COMPONENT_HIGH_OPTION_COUNT
    ? parseInt(process.env.COMPONENT_HIGH_OPTION_COUNT)
    : 12;
  const isHighOptionCount = size(field.options) > highOptionCountNumber;

  let fieldDisplay: any = null;
  switch (field.type) {
    case 'Boolean':
      fieldDisplay = (
        <Controller
          key={field.name + 'Controller'}
          name={field.name}
          control={control}
          rules={{
            validate: (value) => {
              const validator = getValidator(field.name);
              return validator ? validator(value) ?? true : true;
            }
          }}
          render={({ field: { onChange, value } }) => {
            if (booleanAsCheckbox) {
              return (
                <ChilliCheckbox
                  identifier={field.name}
                  label={field.label}
                  onChilliChange={onChange}
                  valueOn={true}
                  value={value}
                  statetype={fieldErrors ? 'error' : undefined}
                  statemessage={fieldErrors?.message ?? 'Please enter a valid value'}
                >
                  <span dangerouslySetInnerHTML={{ __html: field.label }}></span>
                </ChilliCheckbox>
              );
            }
            return (
              <ChilliRadioGroup
                key={field.name}
                identifier={field.name}
                label={displayLabel ? field.heading : ''}
                onChilliChange={onChange}
                value={value}
                options={[
                  { id: true, label: 'Yes' },
                  { id: false, label: 'No' }
                ]}
                variant="button"
                statetype={fieldErrors ? 'error' : undefined}
                statemessage={fieldErrors?.message ?? 'Please enter a valid value'}
              />
            );
          }}
        />
      );
      break;
    case 'String':
      fieldDisplay = (
        <div className="flex flex-col">
          {displayLabel && (
            <div className="text-base font-semibold block text-gray-1100 dark-host:text-white">{field.heading}</div>
          )}
          <Controller
            key={field.name + 'Controller'}
            name={field.name}
            control={control}
            rules={{
              required: { value: field.required, message: 'This is required' },
              validate: (value) => {
                const validator = getValidator(field.name);
                return validator ? validator(value) ?? true : true;
              }
            }}
            render={({ field: { onChange, value } }) => (
              <>
                <p className="text-sm text-gray-700 dark-host:text-gray-200 pb-4">{field.description}</p>
                <ChilliInput
                  identifier={field.name}
                  key={field.name}
                  onChilliChange={onChange}
                  label={field.label}
                  value={value}
                  statetype={fieldErrors ? 'error' : undefined}
                  statemessage={fieldErrors?.message ?? 'Please enter a valid value'}
                />
              </>
            )}
          />
        </div>
      );
      break;
    case 'File':
      fieldDisplay = (
        <Controller
          key={field.name + 'Controller'}
          name={field.name}
          control={control}
          rules={{
            required: { value: field.required, message: 'This is required' },
            validate: (value) => {
              const validator = getValidator(field.name);
              return validator ? validator(value) ?? true : true;
            }
          }}
          render={() => <FileField field={field} userData={userData} />}
        />
      );
      break;
    case 'List':
    default:
      // multi-value field check here and show checkbox or search list
      if (field.cardinality != 1) {
        if (size(field.options) > 25) {
          fieldDisplay = (
            <Controller
              key={field.name + 'Controller'}
              name={field.name}
              control={control}
              rules={{
                required: { value: field.required, message: 'This is required' },
                validate: (value) => {
                  const validator = getValidator(field.name);
                  return validator ? validator(value) ?? true : true;
                }
              }}
              render={({ field: { onChange, value } }) => (
                <>
                  <p className="text-sm text-gray-700 dark-host:text-gray-200 pb-3">{field.description}</p>
                  <ChilliMultiselect
                    identifier={field.name}
                    label={field.label}
                    className={'w-auto text-gray'}
                    typeahead={true}
                    value={value}
                    onChilliChange={onChange}
                    options={JSON.stringify(field.options)}
                    // smaller amount of options will show a button list instead
                    statetype={fieldErrors ? 'error' : undefined}
                    statemessage={fieldErrors?.message ?? 'Please enter a valid value'}
                  />
                </>
              )}
            ></Controller>
          );
          break;
        }

        fieldDisplay = (
          <Controller
            key={field.name + 'Controller'}
            name={field.name}
            control={control}
            rules={{
              required: { value: field.required, message: 'This is required' },
              validate: (value) => {
                const validator = getValidator(field.name);
                return validator ? validator(value) ?? true : true;
              }
            }}
            render={({ field: { onChange, value } }) => (
              <ChilliCheckboxGroup
                identifier={field.name}
                label={displayLabel ? field.heading : ''}
                className={'w-auto text-gray'}
                value={value}
                onChilliChange={onChange}
                description={field.description ?? ''}
                // smaller amount of options will show a button list instead
                variant={!isHighOptionCount ? 'button' : 'default'}
                statetype={fieldErrors ? 'error' : undefined}
                statemessage={fieldErrors?.message ?? 'Please enter a valid value'}
              >
                {field.options!.map((option) => {
                  return (
                    <ChilliCheckbox
                      valueOn={option.id}
                      key={'chilli_cb-' + option.id}
                      variant={!isHighOptionCount ? 'button' : 'default'}
                    >
                      <span className="text-sm font-semibold" dangerouslySetInnerHTML={{ __html: option.label }}></span>
                      {!isEmpty(option.children) && (
                        <div slot="nested">
                          <ChilliCheckboxGroup className="text-sm">
                            {option?.children &&
                              Array.isArray(option?.children) &&
                              option?.children?.map((children: any) => (
                                <ChilliCheckbox key={children.id} valueOn={children.id}>
                                  {children.label}
                                </ChilliCheckbox>
                              ))}
                          </ChilliCheckboxGroup>
                        </div>
                      )}
                    </ChilliCheckbox>
                  );
                })}
              </ChilliCheckboxGroup>
            )}
          ></Controller>
        );
        break;
      }

      // large option count should be a drop down select list
      if (isHighOptionCount) {
        fieldDisplay = (
          <div className="flex flex-col gap-y-1">
            {displayLabel && (
              <div className="text-base font-semibold block text-gray-1100 dark-host:text-white">{field.heading}</div>
            )}
            <Controller
              key={field.name + 'Controller'}
              name={field.name}
              control={control}
              rules={{
                required: { value: field.required, message: 'This is required' },
                validate: (value) => {
                  const validator = getValidator(field.name);
                  return validator ? validator(value) ?? true : true;
                }
              }}
              render={({ field: { onChange, value } }) => (
                <>
                  <p className="text-sm text-gray-700 dark-host:text-gray-200 pb-3">{field.description}</p>
                  <ChilliSelect
                    identifier={field.name}
                    key={field.name}
                    typeahead={true}
                    onChilliChange={onChange}
                    value={value}
                    label={field.label}
                    className={'w-auto text-gray'}
                    options={field.options}
                    statetype={fieldErrors ? 'error' : undefined}
                    statemessage={fieldErrors?.message ?? 'Please enter a valid value'}
                  />
                </>
              )}
            />
          </div>
        );
        break;
      }

      // most options are a button radio group
      fieldDisplay = (
        <Controller
          key={field.name + 'Controller'}
          name={field.name}
          control={control}
          rules={{
            required: { value: field.required, message: 'This is required' },
            validate: (value) => {
              const validator = getValidator(field.name);
              return validator ? validator(value) ?? true : true;
            }
          }}
          render={({ field: { onChange, value } }) => (
            <ChilliRadioGroup
              identifier={field.name}
              key={field.name}
              onChilliChange={onChange}
              label={displayLabel ? field.heading : ''}
              className={'w-auto text-gray'}
              value={value}
              variant={!isHighOptionCount ? 'button' : 'default'}
              options={field.options}
              statetype={fieldErrors ? 'error' : undefined}
              statemessage={fieldErrors?.message ?? 'Please enter a valid value'}
            />
          )}
        />
      );
      break;
  }

  const otherFieldErrors = errors[field.name + '_other'] ?? null;
  // add other field when option is selected
  const otherField = (
    <div className="mt-4 mb-2 w-full">
      <Controller
        key={field.name + '_other'}
        name={field.name + '_other'}
        defaultValue={userData?.otherValue ?? null}
        control={control}
        rules={{
          required: { value: true, message: 'This is required' },
          validate: (value) => {
            const validator = getValidator(field.name);
            return validator ? validator(value) ?? true : true;
          }
        }}
        render={({ field: { onChange, value } }) => (
          <ChilliInput
            identifier={field.name}
            key={field.name + '_other'}
            label={'Please specify'}
            onChilliChange={onChange}
            value={value}
            statetype={otherFieldErrors ? 'error' : undefined}
            statemessage="Please enter a value"
          />
        )}
      />
    </div>
  );

  return (
    <>
      {fieldDisplay}
      {field.otherRefTerm && selectedValue == field.otherRefTerm && otherField}
    </>
  );
};

export default UserserviceField;
